import React from 'react';
import { graphql, Link } from 'gatsby';
import clsx from 'clsx';
import { LinkReference } from '../../graphql-fragments/LinkReference';
import AnchorLink from 'react-anchor-link-smooth-scroll';
import { getReferenceUrl } from '../../utils/utils';
import { CommonSectionProps, SectionBgColor } from '../../templates/Page';

import * as styles from './CtaSection.module.scss';

export const CtaSectionFragment = graphql`
  fragment CtaSection on SanityCtaSection {
    background
    backgroundImage {
      asset {
        url
      }
    }
    title
    button {
      text
      url
      linkReference {
        ...LinkReference
      }
      anchorLink
    }
  }
`;

export type CtaSectionProps = {
  background: 'image' | 'greenGradient';
  backgroundImage?: {
    asset: {
      url: string;
    };
  };
  title: string;
  button: {
    text: string;
  } & (
    | {
        url?: never;
        linkReference?: never;
        anchorLink: string;
      }
    | {
        url?: never;
        linkReference: LinkReference;
        anchorLink?: never;
      }
    | {
        url: string;
        linkReference?: never;
        anchorLink?: never;
      }
  );

  className?: string;
};

export function getSectionBgColor(props: CtaSectionProps): SectionBgColor<'white'> {
  return {
    name: 'white',
    type: 'textured',
  };
}

function CtaSection<PrevSectionColorName extends string>(
  props: CtaSectionProps & CommonSectionProps<PrevSectionColorName>,
): React.ReactElement {
  const { background, backgroundImage, title, button, className } = props;

  return (
    <section
      id={props.sectionID || ''}
      className={clsx(
        styles.sectionContainer,
        background === 'image' && styles.imageBackground,
        className,
      )}
      style={
        background === 'image' && backgroundImage
          ? {
              background: `linear-gradient(0deg, rgba(0, 62, 82, 0.1), rgba(0, 62, 82, 0.1)),
      linear-gradient(0deg, rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.15)),
      linear-gradient(88.33deg, rgba(0, 62, 82, 0.72) 22.23%, rgba(0, 0, 0, 0) 80.84%),
      url(${backgroundImage.asset.url}) center no-repeat`,
              backgroundSize: 'cover',
            }
          : {
              background: 'linear-gradient(180deg, #f6faec 0%, #ffffff 100%)',
            }
      }
    >
      <div className={styles.section}>
        <h2 className={styles.title}>{title}</h2>
        {(button.linkReference || button.url) && (
          <Link
            className={styles.button}
            to={
              button.linkReference
                ? getReferenceUrl(
                    button.linkReference.__typename,
                    button.linkReference.slug.current,
                  )
                : button.url
            }
          >
            {button.text}
          </Link>
        )}
        {button.anchorLink && (
          <AnchorLink href={'#' + button.anchorLink} className={styles.button}>
            {button.text}
          </AnchorLink>
        )}
      </div>
    </section>
  );
}

export default CtaSection;
